// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { createRef, ReactNode, useCallback, useEffect, useState } from "react";
import { css, ThemeProps, withTheme } from "styled-components";

/* Valtech Custom Code - Start */
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { TextAlignment, textAlignmentOptions } from "@store/Framework/Common/Fields/Toolkit/textAlignment";
import { carouselProps } from "@store/Themes/common/props/carousel";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
/* Valtech Custom Code - End */

interface OwnProps {
    id: string;
    extendedStyles?: CarouselStyles;
    title?: string; // Valtech Custom Code
    maxNumberOfColumns: number;
    slides: CarouselSlide[];
    // Valtech Custom Code
    showDots?: boolean;
    titleAlignment?: TextAlignment;
    useSkipNav?: boolean;
}

type Props = OwnProps & ThemeProps<BaseTheme> & HasShellContext;

export interface CarouselSlide {
    id: string;
    renderComponent: (options: CarouselSlideOptions) => ReactNode;
}

export interface CarouselSlideOptions {}

export interface CarouselStyles {
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    // Valtech Custom Code
    prevArrowWrapper?: InjectableCss;
    nextArrowWrapper?: InjectableCss;
    dotsWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    dotButtonSelected?: ButtonPresentationProps;
}

export const carouselStyles: CarouselStyles = {
    titleText: carouselProps.titleText,
    prevArrowWrapper: carouselProps.prevArrowWrapper,
    prevArrowButton: carouselProps.prevArrowButton,
    nextArrowWrapper: carouselProps.nextArrowWrapper,
    nextArrowButton: carouselProps.nextArrowButton,
    carouselWrapper: carouselProps.carouselWrapper,
    carouselContainer: carouselProps.carouselContainer,
    carouselSlidesContainer: carouselProps.carouselSlidesContainer,
    carouselSlide: carouselProps.carouselSlide,
    carouselSlideInner: carouselProps.carouselSlideInner,
    dotsWrapper: carouselProps.dotsWrapper,
    dotButton: carouselProps.dotButton,
    dotButtonSelected: carouselProps.dotButtonSelected,
};

const Carousel = ({
    shellContext,
    theme,
    id,
    extendedStyles,
    title,
    maxNumberOfColumns,
    slides,
    // Valtech Custom Code
    showDots,
    titleAlignment = textAlignmentOptions.center.id,
    useSkipNav = true,
}: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const [styles] = useState(() => mergeToNew(carouselStyles, extendedStyles));

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0); // Valtech Custom Code
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            // Valtech Removed - return
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    /* Valtech Custom Code - Start */
    const getSlidePerBreakpoint = (maxNumber: number) => {
        switch (maxNumber) {
            case 6:
                return [1, 3, 4, 5, 6];
            case 5:
                return [1, 2, 3, 4, 5];
            case 4:
                return [1, 2, 3, 4, 4];
            case 3:
                return [1, 2, 2, 3, 3];
            case 2:
                return [1, 2, 2, 2, 2];
            case 1:
                return [1, 1, 1, 1, 1];
            default:
                return [1, 2, 3, 4, maxNumber];
        }
    };
    /* Valtech Custom Code - End */

    const getSlidesToScroll = useCallback(() => {
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        /* Valtech Custom Code - Start */
        const slidePerBreakpoint = getSlidePerBreakpoint(maxNumberOfColumns);

        let localSlidesToScroll: number;
        const { innerWidth } = window;
        if (innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = slidePerBreakpoint[0];
        } else if (innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = slidePerBreakpoint[1];
        } else if (innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = slidePerBreakpoint[2];
        } else if (innerWidth < theme.breakpoints.values[4]) {
            localSlidesToScroll = slidePerBreakpoint[3];
        } else {
            localSlidesToScroll = slidePerBreakpoint[4];
        }
        /* Valtech Custom Code - End */
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = useState(0);

    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);
    }, [maxNumberOfColumns, windowResizeTime]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        /* Valtech Custom Code - Start */
        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
        /* Valtech Custom Code - End */

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    useEffect(() => {
        if (!embla || !slides || slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            draggable: isTouchDevice(),
            loop: slides.length > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, slides]);

    if (!slides || slides.length === 0) {
        return null;
    }

    /* Valtech Custom Code - Start */
    const dotCount = slidesToScroll > 0 ? Math.ceil(slides.length / slidesToScroll) : 0;

    const getDots = () => {
        const dots = [];

        for (let i = 0; i < dotCount; i++) {
            dots.push(
                <Button
                    key={i} // eslint-disable-line react/no-array-index-key
                    {...(i === selectedIndex ? styles.dotButtonSelected : styles.dotButton)}
                    aria-current={i === selectedIndex}
                    onClick={() => embla && embla.scrollTo(i)}
                >
                    <VisuallyHidden>{translate("Go to slide {0} of {1}", `${i + 1}`, `${dotCount}`)}</VisuallyHidden>
                </Button>,
            );
        }

        return <StyledWrapper {...styles.dotsWrapper}>{dots}</StyledWrapper>;
    };

    const titleCss = css`
        ${styles.titleText?.css}
        ${cssProp("text-align", titleAlignment)}
    `;
    /* Valtech Custom Code - End */

    return (
        <>
            {/* Valtech Moved - SkipNav - down */}
            {/* Valtech Custom Code - Start */}
            {title ? (
                <Typography {...styles.titleText} css={titleCss}>
                    {title}
                </Typography>
            ) : null}
            {useSkipNav && (
                <SkipNav
                    text={translate("Skip Carousel")}
                    extendedStyles={styles.skipCarouselButton}
                    destination={afterCarousel}
                />
            )}
            {/* Valtech Custom Code - End */}
            <StyledWrapper // Valtech Custom Code
                {...styles.carouselWrapper} // Valtech Custom Code
                data-test-selector="carousel"
            >
                {/* Valtech Custom Code - Start */}
                {slides.length > slidesToScroll && (
                    <StyledWrapper {...styles.prevArrowWrapper}>
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                            data-test-selector="prevBtn"
                        >
                            <VisuallyHidden>{translate("Previous slide")}</VisuallyHidden>
                        </Button>
                    </StyledWrapper>
                )}
                {/* Valtech Custom Code - End */}
                {/* Valtech Removed - carouselGridItem */}
                {/* Valtech Removed - carouselWrapper */}
                <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                    <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                        {slides.map(slide => (
                            <StyledWrapper
                                {...styles.carouselSlide}
                                key={slide.id}
                                style={{ width: `calc(100% / ${slidesToScroll})` }}
                            >
                                <StyledWrapper
                                    {...styles.carouselSlideInner}
                                    data-test-key={slide.id}
                                    id={`slide_${slide.id}`}
                                    data-test-selector="carousel_slide"
                                >
                                    {slide.renderComponent({})}
                                </StyledWrapper>
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
                {/* Valtech Custom Code - Start */}
                {slides.length > slidesToScroll && (
                    <StyledWrapper {...styles.nextArrowWrapper}>
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                            data-test-selector="nextBtn"
                        >
                            <VisuallyHidden>{translate("Next slide")}</VisuallyHidden>
                        </Button>
                    </StyledWrapper>
                )}
                {/* Valtech Custom Code - End */}
            </StyledWrapper>
            {showDots && dotCount > 1 ? getDots() : null /* Valtech Custom Code */}
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

Carousel.defaultProps = {
    maxNumberOfColumns: 4,
};

export default withIsInShell(withTheme(Carousel));
